import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProgress, saveProgress } from '../services/api';
import WizardLayout from '../components/wizard/WizardLayout';
import { computeStepStatuses } from '../components/wizard/stepStatus';
import {
  STEPS,
  isStepVisible,
  lastVisibleIndex,
  nextVisibleIndex,
  stepVisibility,
  visibleStepNumber,
} from '../components/wizard/steps';
import {
  firstIncompleteStep,
  hasSpouse,
  validateStep,
} from '../components/wizard/validation';
import StepWelcome from '../components/wizard/StepWelcome';
import StepHousehold from '../components/wizard/StepHousehold';
import StepContact from '../components/wizard/StepContact';
import StepEmployment from '../components/wizard/StepEmployment';
import StepCompliance from '../components/wizard/StepCompliance';
import StepTrustedContact from '../components/wizard/StepTrustedContact';
import StepBeneficiaries from '../components/wizard/StepBeneficiaries';
import StepReview from '../components/wizard/StepReview';

function scrollContentToTop() {
  document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
}

export default function WizardPage() {
  const { client, logout } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [clientType, setClientType] = useState('hff');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    async function load() {
      try {
        const data = await getProgress();
        setCurrentStep(data.current_step || 0);
        setFormData(data.form_data || {});
        setClientType(data.client_type || 'hff');
      } catch (err) {
        console.error('Failed to load progress:', err);
        setError('Unable to load your saved progress. Please reload the page.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const persist = useCallback(
    async (step, data, completed = false) => {
      setSaving(true);
      try {
        await saveProgress(step, data, completed, client?.client_id || null);
        setError(null);
        return true;
      } catch (err) {
        console.error('Save failed:', err);
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 409) {
          setError(
            detail
            || 'Session identity mismatch. Please reload the form using your original access link.',
          );
        } else {
          setError('Could not save — please check your connection.');
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [client],
  );

  function sectionData(key) {
    return formData[key] || {};
  }

  function updateSection(key) {
    return (updated) => {
      setFormData((prev) => ({ ...prev, [key]: updated }));
    };
  }

  function goTo(index, data = formData) {
    setCurrentStep(index);
    persist(index, data);
    scrollContentToTop();
  }

  function goNext() {
    const step = STEPS[currentStep];
    const stepError = validateStep(step.key, formData);
    if (stepError) {
      setError(stepError);
      return;
    }

    const next = nextVisibleIndex(currentStep, +1, formData);
    if (next >= STEPS.length) {
      // Final submit — re-check the whole form in case the client changed
      // their spouse answer after filling things in.
      const blocking = firstIncompleteStep(STEPS, formData, isStepVisible);
      if (blocking) {
        setCurrentStep(blocking.index);
        setError(blocking.message);
        scrollContentToTop();
        return;
      }
      persist(currentStep, formData, true).then((ok) => {
        if (ok) navigate('/complete', { replace: true });
      });
      return;
    }

    setError(null);
    goTo(next);
  }

  function goBack() {
    const prev = nextVisibleIndex(currentStep, -1, formData);
    if (prev < 0) return;
    setError(null);
    goTo(prev);
  }

  function jumpTo(index) {
    if (index === currentStep) return;
    if (index < 0 || index >= STEPS.length) return;
    if (!isStepVisible(STEPS[index], formData)) return;
    setError(null);
    goTo(index);
  }

  function jumpToKey(key) {
    jumpTo(STEPS.findIndex((s) => s.key === key));
  }

  function renderStep() {
    const step = STEPS[currentStep];
    if (!step) return null;

    const spousePresent = hasSpouse(formData);
    const person = step.person === 'spouse' ? 'spouse' : 'primary';
    const number = visibleStepNumber(currentStep, formData);

    const shared = {
      number,
      person,
      data: sectionData(step.key),
      onChange: updateSection(step.key),
    };

    // `updateSection` only ever writes one step key, so the "same mailing
    // address" shortcuts need their source handed in. Each person copies
    // their own home address, not the first person's.
    const homeAddress = formData[
      person === 'spouse' ? 'spouse_contact' : 'contact'
    ]?.address;

    switch (step.key) {
      case 'welcome':
        return <StepWelcome clientName={client?.name || ''} />;
      case 'household':
        return (
          <StepHousehold
            number={number}
            data={sectionData('household')}
            onChange={updateSection('household')}
          />
        );
      case 'contact':
      case 'spouse_contact':
        return <StepContact {...shared} />;
      case 'employment':
      case 'spouse_employment':
        return <StepEmployment {...shared} />;
      case 'compliance':
      case 'spouse_compliance':
        return <StepCompliance {...shared} />;
      case 'trusted_contact':
      case 'spouse_trusted_contact':
        return <StepTrustedContact {...shared} homeAddress={homeAddress} />;
      case 'beneficiaries':
      case 'spouse_beneficiaries':
        return (
          <StepBeneficiaries
            {...shared}
            spousePresent={spousePresent}
            homeAddress={homeAddress}
            primaryBeneficiaries={formData.beneficiaries}
          />
        );
      case 'review':
        return (
          <StepReview
            number={number}
            formData={formData}
            steps={STEPS}
            onJump={jumpToKey}
          />
        );
      default:
        return null;
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-dark" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  const stepVisible = stepVisibility(formData);
  const themeClass = clientType === 'rebellionaire' ? 'theme-rebellionaire' : '';

  return (
    <div className={themeClass}>
      <WizardLayout
        currentStep={currentStep}
        steps={STEPS}
        onBack={goBack}
        onNext={goNext}
        isFirst={currentStep === 0}
        isLast={currentStep === lastVisibleIndex(formData)}
        saving={saving}
        submitLabel="Submit"
        onStepClick={jumpTo}
        stepStatuses={computeStepStatuses(STEPS, formData)}
        stepVisible={stepVisible}
        clientName={client?.name || ''}
        onLogout={logout}
      >
        {renderStep()}
      </WizardLayout>

      {error && (
        <div
          className="alert alert-warning position-fixed bottom-0 start-50 translate-middle-x mb-3 shadow"
          style={{ zIndex: 9999, maxWidth: 640 }}
        >
          <div className="d-flex align-items-start gap-2">
            <i className="bi bi-exclamation-triangle-fill mt-1" />
            <span className="small flex-fill">{error}</span>
            <button
              type="button"
              className="btn-close btn-sm"
              onClick={() => setError(null)}
              aria-label="Dismiss"
            />
          </div>
        </div>
      )}
    </div>
  );
}
